import React from 'react';

import CustomModal from 'components/CustomModal';
import { Button } from 'components/Button';

import { OrgUnit, findUnit, nameTakenMessage, nameTakenUnder, parentOf } from '../types';

import UnitNameField from './UnitNameField';
import { DialogContent } from './styles';

export interface EditUnitModalProps {
  open: boolean;
  units: OrgUnit[];
  unitId: string | null;
  onCancel: () => void;
  onConfirm: (name: string) => Promise<void>;
}

const EditUnitModal: React.FunctionComponent<EditUnitModalProps> = ({ open, units, unitId, onCancel, onConfirm }) => {
  const unit = unitId ? findUnit(units, unitId) : null;
  const [name, setName] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setName(unit?.name ?? '');
    // Only on opening: after saving, the new name must not refill the field as it closes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trimmed = name.trim();
  const parentId = unit ? (parentOf(units, unit.uuid)?.uuid ?? null) : null;
  const error =
    unit && trimmed && nameTakenUnder(units, parentId, trimmed, unit.uuid)
      ? nameTakenMessage(units, parentId, trimmed)
      : null;
  const cannotSave = !unit || trimmed.length === 0 || trimmed === unit.name || error !== null;

  const handleConfirm = (): void => {
    if (cannotSave) return;
    onConfirm(trimmed);
  };

  return (
    <CustomModal
      openModal={open}
      onClose={onCancel}
      modalTitle="Rename unit"
      modalWidth="sm"
      cancelButton={
        <Button onClick={onCancel} variant="outlined" size="large">
          Cancel
        </Button>
      }
      submitButton={
        <Button onClick={handleConfirm} disabled={cannotSave} variant="contained" size="large">
          Rename
        </Button>
      }
    >
      <DialogContent data-testid="edit-unit-dialog">
        <UnitNameField value={name} error={error} onChange={setName} onSubmit={handleConfirm} />
      </DialogContent>
    </CustomModal>
  );
};

export default EditUnitModal;
